import { ProcessCheckoutPaymentButton } from '@/components/actions/payouts/ProcessCheckoutPaymentButton';
import { Panel } from '@/components/panel/panel';
import { StripeConfigurationAlert } from '@/components/payment-methods/StripeConfigurationAlert';
import { Money } from '@/components/text';
import { DataListItem, DataListRoot } from '@/components/ui/data-list';
import {
  PopoverBody,
  PopoverContent,
  PopoverHeader,
  PopoverRoot,
  PopoverTrigger,
} from '@/components/ui/popover';
import { toaster } from '@/components/ui/toaster';
import { useGetPayoutBatchPaymentDetails } from '@/hooks/queries/use-fetch-payout-payment-details-query';
import { isStripeConfigured, stripePromise } from '@/libs/stripe-client';
import { usePayoutCheckoutData, usePayoutCheckoutStore } from '@/stores/payout-checkout-store';
import { Flex, IconButton, Text } from '@chakra-ui/react';
import { Elements } from '@stripe/react-stripe-js';
import type { StripeElementsOptions } from '@stripe/stripe-js';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { PriceSummaryPanelSkeleton } from './PriceSummaryPanelSkeleton';

type PriceSummaryPanelProps = object;

const renderPaymentButton = ({
  paymentIntentClientSecret,
}: {
  paymentIntentClientSecret: string;
}) => {
  if (!isStripeConfigured) {
    return <StripeConfigurationAlert mt={3} />;
  }

  const options: StripeElementsOptions = {
    clientSecret: paymentIntentClientSecret ?? undefined,
  };

  return (
    <Elements stripe={stripePromise} options={options}>
      <ProcessCheckoutPaymentButton mt={3} />
    </Elements>
  );
};

export const PriceSummaryPanel: React.FC<PriceSummaryPanelProps> = () => {
  const { t } = useTranslation();
  const { payout, paymentIntentClientSecret: secretFromStore } = usePayoutCheckoutData();

  const setPaymentIntentClientSecret = usePayoutCheckoutStore(
    (state) => state.setPaymentIntentClientSecret
  );

  const shouldFetchSecret = !!payout?.id && !secretFromStore;

  const {
    data: paymentDetails,
    isLoading: isLoadingSecret,
    error: secretError,
    isError: isSecretError,
  } = useGetPayoutBatchPaymentDetails(payout?.id, {
    enabled: shouldFetchSecret,
  });

  useEffect(() => {
    if (paymentDetails?.paymentIntentClientSecret) {
      console.log('Secret fetched via API, storing...');
      setPaymentIntentClientSecret(paymentDetails.paymentIntentClientSecret);
    }
  }, [paymentDetails, setPaymentIntentClientSecret]);

  useEffect(() => {
    if (isSecretError) {
      const errorData = secretError as any;
      toaster.create({
        title: 'Failed to load payment details',
        description:
          errorData?.response?.data?.message ||
          errorData.message ||
          'Could not retrieve payment information. Please try refreshing.',
        type: 'error',
      });
    }
  }, [isSecretError, secretError]);

  const isLoading = !payout || isLoadingSecret;

  // Use backend-calculated VAT
  const vatAmount = payout?.vatAmount?.amount ?? 0;

  // Calculate total amount including tax
  const totalAmountInclTax = payout
    ? payout.totalCommissionAmount.amount +
      (payout.totalCollectedPlatformFee?.amount ?? 0) +
      vatAmount
    : 0;

  // The rate this batch actually carries, read from the two amounts already on
  // screen. A fixed percentage in the label would tell a brand on a negotiated
  // rate that it paid one it never did.
  const platformFeeRate =
    payout?.totalCollectedPlatformFee && payout.totalCommissionAmount.amount > 0
      ? payout.totalCollectedPlatformFee.amount / payout.totalCommissionAmount.amount
      : null;

  const platformFeeLabel =
    platformFeeRate === null
      ? t('payouts.priceSummary.platformFee')
      : t('payouts.priceSummary.platformFeeWithRate', {
          rate: (platformFeeRate * 100).toFixed(2).replace(/\.?0+$/, ''),
        });

  const actualContent = payout && (
    <>
      <DataListRoot orientation="horizontal" gap={3}>
        <DataListItem
          label={t('payouts.priceSummary.amountPaidToAffiliates')}
          labelColor="gray.950"
          labelFontWeight="normal"
          value={
            <Flex flex={1} justifyContent="flex-end">
              <Money
                fontSize="14px"
                fontWeight="normal"
                value={payout.totalCommissionAmount.amount}
                currency={payout.totalCommissionAmount.currencyCode}
              />
            </Flex>
          }
        />
        <DataListItem
          labelColor="gray.950"
          labelFontWeight="normal"
          label={
            <Flex align="center" gap={1}>
              <Text>{platformFeeLabel}</Text>
              <PopoverRoot positioning={{ placement: 'top' }}>
                <PopoverTrigger asChild>
                  <IconButton
                    variant="ghost"
                    size="2xs"
                    aria-label={t('payouts.priceSummary.feeInfoAriaLabel')}
                  >
                    <Text fontSize="sm" color="gray.500">
                      ⓘ
                    </Text>
                  </IconButton>
                </PopoverTrigger>
                <PopoverContent maxW="300px">
                  <PopoverHeader fontWeight="semibold" fontSize="sm">
                    {t('payouts.priceSummary.feeExplanation.title')}
                  </PopoverHeader>
                  <PopoverBody fontSize="sm">
                    {t('payouts.priceSummary.feeExplanation.description')}
                  </PopoverBody>
                </PopoverContent>
              </PopoverRoot>
            </Flex>
          }
          value={
            payout.totalCollectedPlatformFee && (
              <Flex flex={1} justifyContent="flex-end">
                <Money
                  fontSize="14px"
                  value={payout.totalCollectedPlatformFee.amount}
                  currency={payout.totalCollectedPlatformFee.currencyCode}
                  textProps={{ fontWeight: 'normal' }}
                />
              </Flex>
            )
          }
        />
        <DataListItem
          labelColor="gray.950"
          labelFontWeight="normal"
          label={t('payouts.priceSummary.vatOnPlatformFee')}
          value={
            <Flex flex={1} justifyContent="flex-end">
              <Money
                fontSize="14px"
                fontWeight="normal"
                value={vatAmount}
                currency={payout.totalCommissionAmount.currencyCode}
              />
            </Flex>
          }
        />
        <DataListItem
          mt={2}
          labelColor="gray.950"
          label={
            <Text fontWeight="medium" textTransform="uppercase" fontSize="13px">
              {t('payouts.priceSummary.totalAmountInclTax')}
            </Text>
          }
          value={
            <Flex flex={1} justifyContent="flex-end">
              <Money
                fontSize="15px"
                fontWeight="medium"
                value={totalAmountInclTax}
                currency={payout.totalCommissionAmount.currencyCode}
              />
            </Flex>
          }
        />
      </DataListRoot>
      {secretFromStore &&
        renderPaymentButton({
          paymentIntentClientSecret: secretFromStore,
        })}
    </>
  );

  return (
    <Panel title={t('payouts.priceSummary.title')} rounded="xl">
      {isLoading ? <PriceSummaryPanelSkeleton /> : actualContent}
    </Panel>
  );
};
